import { useEffect, useRef, useState } from "react";
import "@/App.css";
import {
  Menu,
  X,
  Play,
  Camera,
  Clapperboard,
  Megaphone,
  Share2,
  MessageCircle,
  Instagram,
  Facebook,
  ImageIcon,
  ArrowUpRight,
} from "lucide-react";

const LOGO = "/assets/logo.png"; // swap this file to replace the logo site-wide
const PHONE_DISPLAY = "07759 543795";
const WHATSAPP_URL =
  "https://wa.me/447759543795?text=Hi%20Crosby%20Media%20Productions%2C%20I%27d%20like%20to%20chat%20about%20a%20project.";
const INSTAGRAM_URL =
  "https://www.instagram.com/crosbymediaproductions?igsh=MTh5MDJhanU3Zzl5aw%3D%3D&utm_source=qr";
const FACEBOOK_URL = "https://www.facebook.com/share/1SjCSwLGvw/?mibextid=wwXIfr";

const NAV = [
  { label: "Home", href: "#home" },
  { label: "Services", href: "#services" },
  { label: "My Work", href: "#work" },
  { label: "About", href: "#about" },
  { label: "Contact", href: "#contact" },
];

const SERVICES = [
  {
    icon: Clapperboard,
    title: "Video Production",
    desc: "Cinematic video content for brands, events and stories that deserve to be seen.",
  },
  {
    icon: Camera,
    title: "Photography",
    desc: "Professional photography that captures your moments at their very best.",
  },
  {
    icon: Megaphone,
    title: "Promotional Content",
    desc: "Promo visuals built to get your project noticed and remembered.",
  },
  {
    icon: Share2,
    title: "Social Media Content",
    desc: "Scroll-stopping content made for Instagram, Facebook and beyond.",
  },
];

// Add new gallery items here — { type: "video", src, poster, title } or { type: "image", src, title }
const WORK = [
  {
    type: "video",
    src: "/assets/work-1.mp4",
    poster: "/assets/poster-1.jpg",
    title: "Crosby Media Productions — Film 01",
    span: "feature",
  },
  { type: "image", src: "/assets/photo-dj.webp", title: "Event Photography", span: "side" },
  {
    type: "video",
    src: "/assets/work-2.mp4",
    poster: "/assets/poster-2.jpg",
    title: "Crosby Media Productions — Film 02",
    span: "side",
  },
  { type: "image", src: "/assets/photo-mirror.webp", title: "Bridal Photography", span: "third" },
  { type: "image", src: "/assets/photo-estate.webp", title: "Wedding Photography", span: "third" },
  { type: "image", src: "/assets/photo-bench.webp", title: "Wedding Portraits", span: "third" },
  { type: "image", src: "/assets/photo-reveal.webp", title: "Gender Reveal", span: "third" },
  {
    type: "video",
    src: "/assets/utopia-1.mp4",
    poster: "/assets/poster-utopia-1.jpg",
    title: "The Utopia Frequency — Live",
    span: "third",
  },
  {
    type: "video",
    src: "/assets/utopia-2.mp4",
    poster: "/assets/poster-utopia-2.jpg",
    title: "The Utopia Frequency — Highlights",
    span: "third",
  },
  {
    type: "video",
    src: "/assets/drums.mp4",
    poster: "/assets/poster-drums.jpg",
    title: "Live Event — Drum Circle",
    span: "third",
  },
  {
    type: "video",
    src: "/assets/nicky.mp4",
    poster: "/assets/poster-nicky.jpg",
    title: "Nicky Blackmarket — Live Set",
    span: "third",
  },
  {
    type: "video",
    src: "/assets/expo.mp4",
    poster: "/assets/poster-expo.jpg",
    title: "Business Expo — Event Coverage",
    span: "third",
  },
];

const STRIP = ["VIDEO PRODUCTION", "PHOTOGRAPHY", "SOCIAL CONTENT", "CREATIVE MEDIA"];

const Reveal = ({ children, className = "", delay = 0 }) => {
  const ref = useRef(null);
  const [vis, setVis] = useState(false);
  useEffect(() => {
    const el = ref.current;
    const obs = new IntersectionObserver(
      ([e]) => {
        if (e.isIntersecting) {
          setVis(true);
          obs.disconnect();
        }
      },
      { threshold: 0.12 }
    );
    if (el) obs.observe(el);
    return () => obs.disconnect();
  }, []);
  return (
    <div
      ref={ref}
      style={{ transitionDelay: `${delay}ms` }}
      className={`reveal ${vis ? "reveal-visible" : ""} ${className}`}
    >
      {children}
    </div>
  );
};

const Header = () => {
  const [open, setOpen] = useState(false);
  const [scrolled, setScrolled] = useState(false);
  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 40);
    window.addEventListener("scroll", onScroll, { passive: true });
    return () => window.removeEventListener("scroll", onScroll);
  }, []);
  return (
    <header
      data-testid="site-header"
      className={`fixed top-0 left-0 right-0 z-40 border-b border-white/5 backdrop-blur-xl transition-colors duration-300 ${
        scrolled ? "bg-[#05070c]/90" : "bg-[#05070c]/70"
      }`}
    >
      <div
        className={`mx-auto flex max-w-7xl items-center justify-between px-5 transition-all duration-300 md:px-8 ${
          scrolled ? "h-[62px] md:h-[72px]" : "h-[84px] md:h-[100px]"
        }`}
      >
        <a href="#home" data-testid="header-logo-link" className="flex shrink-0 items-center">
          <img
            src={LOGO}
            alt="Crosby Media Productions"
            data-testid="header-logo"
            className={`w-auto mix-blend-screen transition-all duration-300 ${
              scrolled ? "h-[50px] md:h-[60px]" : "h-[66px] md:h-[84px]"
            }`}
          />
        </a>
        <nav className="hidden items-center gap-9 lg:flex" data-testid="desktop-nav">
          {NAV.map((n) => (
            <a
              key={n.href}
              href={n.href}
              data-testid={`nav-${n.label.toLowerCase().replace(" ", "-")}`}
              className="nav-link text-[13px] font-medium uppercase tracking-[0.18em] text-white/60 transition-colors duration-200 hover:text-white"
            >
              {n.label}
            </a>
          ))}
        </nav>
        <div className="flex items-center gap-4">
          <a
            href="#contact"
            data-testid="header-contact-button"
            className="hidden rounded-full border border-cyan-400/50 px-6 py-2.5 text-[12px] font-semibold uppercase tracking-[0.15em] text-cyan-300 transition-all duration-300 hover:border-cyan-300 hover:bg-cyan-400/10 hover:shadow-[0_0_24px_-6px_rgba(34,211,238,0.5)] sm:block"
          >
            Contact Me
          </a>
          <button
            data-testid="mobile-menu-button"
            className="text-white lg:hidden"
            onClick={() => setOpen(!open)}
            aria-label="Toggle menu"
          >
            {open ? <X size={26} /> : <Menu size={26} />}
          </button>
        </div>
      </div>
      {open && (
        <nav
          data-testid="mobile-nav"
          className="border-t border-white/5 bg-[#05070c]/95 px-6 py-4 backdrop-blur-xl lg:hidden"
        >
          {NAV.map((n) => (
            <a
              key={n.href}
              href={n.href}
              data-testid={`mobile-nav-${n.label.toLowerCase().replace(" ", "-")}`}
              onClick={() => setOpen(false)}
              className="block border-b border-white/5 py-4 text-sm font-medium uppercase tracking-[0.18em] text-white/75 last:border-0"
            >
              {n.label}
            </a>
          ))}
          <a
            href="#contact"
            data-testid="mobile-nav-contact-button"
            onClick={() => setOpen(false)}
            className="mt-4 block rounded-full border border-cyan-400/50 py-3 text-center text-[12px] font-semibold uppercase tracking-[0.15em] text-cyan-300"
          >
            Contact Me
          </a>
        </nav>
      )}
    </header>
  );
};

const Hero = ({ onPlay }) => (
  <section
    id="home"
    data-testid="hero-section"
    className="hero-bg noise relative overflow-hidden pt-[112px] md:pt-[136px]"
  >
    <div className="relative z-10 mx-auto grid max-w-7xl items-center gap-10 px-5 pb-16 md:px-8 lg:grid-cols-12 lg:gap-8 lg:pb-24">
      <div className="lg:col-span-7">
        <Reveal>
          <p
            data-testid="hero-kicker"
            className="mb-5 flex items-center gap-3 text-[11px] font-semibold uppercase tracking-[0.4em] text-cyan-300"
          >
            <span className="inline-block h-px w-10 bg-gradient-to-r from-cyan-400 to-fuchsia-500" />
            Crosby Media Productions
          </p>
        </Reveal>
        <Reveal delay={100}>
          <h1
            data-testid="hero-headline"
            className="font-display text-[17vw] leading-[0.92] text-white sm:text-7xl lg:text-[6.5rem] xl:text-[7.5rem]"
          >
            BRING YOUR
            <br />
            <span className="text-glow-cyan">VISION</span> TO
            <br />
            <span className="text-glow-pink">LIFE.</span>
          </h1>
        </Reveal>
        <Reveal delay={200}>
          <p
            data-testid="hero-subtext"
            className="mt-5 max-w-md text-base leading-relaxed text-white/55 md:text-lg"
          >
            Professional visual content created to capture attention, tell stories
            and bring ideas to life.
          </p>
        </Reveal>
        <Reveal delay={300}>
          <div className="mt-8 flex flex-col gap-3 sm:flex-row sm:flex-wrap sm:gap-4">
            <a
              href="#work"
              data-testid="hero-view-work-button"
              className="group flex h-14 items-center justify-center gap-2 rounded-xl bg-cyan-400 px-8 text-[13px] font-bold uppercase tracking-[0.15em] text-[#05070c] transition-all duration-300 hover:bg-cyan-300 hover:shadow-[0_0_35px_-8px_rgba(34,211,238,0.7)] sm:h-[60px]"
            >
              View My Work
              <ArrowUpRight size={16} className="transition-transform duration-300 group-hover:translate-x-0.5 group-hover:-translate-y-0.5" />
            </a>
            <a
              href="#contact"
              data-testid="hero-contact-button"
              className="flex h-14 items-center justify-center rounded-xl border border-fuchsia-500/50 px-8 text-[13px] font-bold uppercase tracking-[0.15em] text-white transition-all duration-300 hover:border-fuchsia-400 hover:bg-fuchsia-500/10 hover:shadow-[0_0_35px_-8px_rgba(236,72,153,0.5)] sm:h-[60px]"
            >
              Get In Touch
            </a>
          </div>
          <a
            href={WHATSAPP_URL}
            target="_blank"
            rel="noopener noreferrer"
            data-testid="hero-whatsapp-link"
            className="mt-5 inline-flex items-center gap-2 text-sm text-white/55 transition-colors duration-200 hover:text-cyan-300"
          >
            <MessageCircle size={15} /> or message me on WhatsApp
          </a>
        </Reveal>
      </div>
      <div className="lg:col-span-5">
        <Reveal delay={250}>
          <button
            data-testid="hero-showreel-card"
            onClick={() =>
              onPlay({ src: "/assets/work-1.mp4", title: "Crosby Media Productions — Showreel" })
            }
            className="group relative mx-auto block w-full max-w-sm lg:max-w-none lg:rotate-[1.5deg] lg:transition-transform lg:duration-500 lg:hover:rotate-0"
            aria-label="Watch showreel"
          >
            <span className="absolute -inset-6 -z-10 rounded-[2rem] bg-gradient-to-br from-cyan-500/30 via-transparent to-fuchsia-500/30 blur-2xl" />
            <span className="relative block overflow-hidden rounded-2xl border border-white/10 shadow-[0_30px_80px_-20px_rgba(0,0,0,0.8)]">
              <img
                src="/assets/poster-1.jpg"
                alt="Featured work by Crosby Media Productions"
                className="aspect-[4/5] w-full object-cover transition-transform duration-700 group-hover:scale-[1.03] lg:aspect-[3/4]"
              />
              <span className="absolute inset-0 bg-gradient-to-t from-black/70 via-black/10 to-black/30" />
              <span className="absolute top-5 left-5 rounded-full border border-white/20 bg-black/40 px-4 py-1.5 text-[10px] font-semibold uppercase tracking-[0.25em] text-white/85 backdrop-blur-sm">
                Featured Work
              </span>
              <span className="absolute inset-0 flex items-center justify-center">
                <span className="flex h-20 w-20 items-center justify-center rounded-full border border-white/40 bg-black/40 backdrop-blur-md transition-all duration-300 group-hover:scale-110 group-hover:border-cyan-300 group-hover:shadow-[0_0_40px_-5px_rgba(34,211,238,0.6)]">
                  <Play size={28} className="ml-1 text-white" fill="white" />
                </span>
              </span>
              <span className="absolute bottom-5 left-5 flex items-center gap-2 text-[11px] font-semibold uppercase tracking-[0.25em] text-white/85">
                <span className="inline-block h-1.5 w-1.5 rounded-full bg-fuchsia-500" />
                Watch Showreel
              </span>
            </span>
          </button>
        </Reveal>
      </div>
    </div>
  </section>
);

const Strip = () => (
  <div data-testid="media-strip" className="overflow-hidden border-y border-white/5 bg-white/[0.015] py-5">
    <div className="marquee-track flex w-max items-center gap-10 whitespace-nowrap">
      {[...STRIP, ...STRIP, ...STRIP, ...STRIP].map((s, i) => (
        <span key={i} className="flex items-center gap-10">
          <span className="font-display text-xl tracking-wide text-white/25">{s}</span>
          <span
            className={`h-1.5 w-1.5 rounded-full ${i % 2 === 0 ? "bg-cyan-400/50" : "bg-fuchsia-500/50"}`}
          />
        </span>
      ))}
    </div>
  </div>
);

const Services = ({ onView }) => (
  <section id="services" data-testid="services-section" className="py-24 md:py-32">
    <div className="mx-auto max-w-7xl px-5 md:px-8">
      <Reveal>
        <div className="flex flex-col gap-6 md:flex-row md:items-end md:justify-between">
          <div>
            <p className="text-[11px] font-semibold uppercase tracking-[0.4em] text-fuchsia-400">
              Services
            </p>
            <h2
              data-testid="services-heading"
              className="font-display mt-4 text-5xl text-white sm:text-6xl lg:text-7xl"
            >
              WHAT I DO
            </h2>
          </div>
          <p className="max-w-sm text-sm leading-relaxed text-white/50 md:text-base">
            Creative media designed to make brands, people and projects stand out.
          </p>
        </div>
      </Reveal>
      <div className="mt-14 grid gap-5 sm:grid-cols-2 lg:grid-cols-4">
        {SERVICES.map((s, i) => (
          <Reveal key={s.title} delay={i * 100}>
            <div
              data-testid={`service-card-${s.title.toLowerCase().replace(/[^a-z]+/g, "-")}`}
              className="service-card group relative h-full overflow-hidden rounded-2xl border border-white/[0.07] bg-gradient-to-b from-white/[0.04] to-transparent p-7 transition-all duration-300 hover:border-cyan-400/30 md:p-8"
            >
              <div className="flex items-start justify-between">
                <s.icon
                  size={28}
                  strokeWidth={1.5}
                  className="text-cyan-300 transition-colors duration-300 group-hover:text-fuchsia-400"
                />
              </div>
              <h3 className="font-display mt-14 text-2xl tracking-wide text-white md:text-3xl">
                {s.title.toUpperCase()}
              </h3>
              <p className="mt-3 text-sm leading-relaxed text-white/50">{s.desc}</p>
              <span className="mt-6 block h-px w-full bg-gradient-to-r from-cyan-400/40 via-fuchsia-500/40 to-transparent opacity-0 transition-opacity duration-300 group-hover:opacity-100" />
            </div>
          </Reveal>
        ))}
      </div>
      <Reveal delay={200}>
        <button
          data-testid="services-packages-image"
          onClick={() => onView({ type: "image", src: "/assets/packages.webp", title: "Crosby Media Productions — Content Packages" })}
          aria-label="View content packages"
          className="group relative mx-auto mt-14 block w-full max-w-2xl overflow-hidden rounded-2xl border border-white/[0.07] transition-colors duration-300 hover:border-cyan-400/30"
        >
          <img
            src="/assets/packages.webp"
            alt="Crosby Media Productions content packages and pricing"
            loading="lazy"
            className="w-full transition-transform duration-700 group-hover:scale-[1.02]"
          />
          <span className="absolute bottom-4 left-4 rounded-full border border-white/20 bg-black/50 px-4 py-1.5 text-[10px] font-semibold uppercase tracking-[0.25em] text-white/85 backdrop-blur-sm">
            Content Packages — Tap To View
          </span>
        </button>
      </Reveal>
    </div>
  </section>
);

const Lightbox = ({ item, onClose }) => {
  useEffect(() => {
    const onKey = (e) => e.key === "Escape" && onClose();
    window.addEventListener("keydown", onKey);
    document.body.style.overflow = "hidden";
    return () => {
      window.removeEventListener("keydown", onKey);
      document.body.style.overflow = "";
    };
  }, [onClose]);

  if (!item) return null;
  return (
    <div
      data-testid="video-lightbox"
      className="fixed inset-0 z-50 flex items-center justify-center bg-black/95 p-4"
      onClick={onClose}
    >
      <button
        data-testid="lightbox-close-button"
        onClick={onClose}
        aria-label="Close video"
        className="absolute top-5 right-5 rounded-full border border-white/20 p-2.5 text-white transition-colors hover:border-cyan-400 hover:text-cyan-300"
      >
        <X size={22} />
      </button>
      <div className="max-h-full w-full max-w-4xl" onClick={(e) => e.stopPropagation()}>
        {item.type === "image" ? (
          <img
            data-testid="lightbox-image"
            src={item.src}
            alt={item.title}
            className="mx-auto max-h-[82vh] w-auto max-w-full rounded-lg"
          />
        ) : (
          <video
            data-testid="lightbox-video"
            src={item.src}
            controls
            autoPlay
            playsInline
            className="mx-auto max-h-[82vh] w-auto max-w-full rounded-lg"
          />
        )}
        <p className="mt-3 text-center text-xs tracking-wide text-white/40">{item.title}</p>
      </div>
    </div>
  );
};

const WorkCard = ({ item, index, onPlay }) => {
  if (item.type === "video") {
    const aspect =
      item.span === "feature" ? "aspect-[4/5] lg:aspect-auto lg:h-full" : "aspect-[4/3]";
    return (
      <button
        data-testid={`work-video-${index}`}
        onClick={() => onPlay(item)}
        aria-label={`Play ${item.title}`}
        className={`group relative block w-full overflow-hidden rounded-2xl border border-white/[0.07] ${aspect}`}
      >
        <img
          src={item.poster}
          alt={item.title}
          loading="lazy"
          className="absolute inset-0 h-full w-full object-cover transition-transform duration-700 group-hover:scale-[1.04]"
        />
        <span className="absolute inset-0 bg-gradient-to-t from-black/60 via-transparent to-black/20 transition-opacity duration-300 group-hover:opacity-70" />
        <span className="absolute top-4 left-4 rounded-full border border-white/15 bg-black/40 px-3 py-1 text-[9px] font-semibold uppercase tracking-[0.25em] text-white/80 backdrop-blur-sm">
          Video
        </span>
        <span className="absolute inset-0 flex items-center justify-center">
          <span
            className={`flex items-center justify-center rounded-full border border-white/40 bg-black/40 backdrop-blur-md transition-all duration-300 group-hover:scale-110 group-hover:border-cyan-300 group-hover:shadow-[0_0_35px_-5px_rgba(34,211,238,0.6)] ${
              item.span === "feature" ? "h-20 w-20" : "h-14 w-14"
            }`}
          >
            <Play size={item.span === "feature" ? 26 : 20} className="ml-0.5 text-white" fill="white" />
          </span>
        </span>
      </button>
    );
  }
  if (item.type === "image") {
    return (
      <button
        data-testid={`work-image-${index}`}
        onClick={() => onPlay(item)}
        aria-label={`View ${item.title}`}
        className="group relative block aspect-[4/3] w-full overflow-hidden rounded-2xl border border-white/[0.07]"
      >
        <img
          src={item.src}
          alt={item.title}
          loading="lazy"
          className="absolute inset-0 h-full w-full object-cover transition-transform duration-700 group-hover:scale-[1.04]"
        />
        <span className="absolute inset-0 bg-gradient-to-t from-black/50 via-transparent to-transparent opacity-60 transition-opacity duration-300 group-hover:opacity-30" />
        <span className="absolute top-4 left-4 rounded-full border border-white/15 bg-black/40 px-3 py-1 text-[9px] font-semibold uppercase tracking-[0.25em] text-white/80 backdrop-blur-sm">
          Photo
        </span>
      </button>
    );
  }
  const aspect = item.span === "third" ? "aspect-[4/3]" : "aspect-[4/3]";
  return (
    <div
      data-testid={`work-placeholder-${index}`}
      className={`relative flex w-full flex-col items-center justify-center gap-3 overflow-hidden rounded-2xl border border-dashed border-white/10 bg-white/[0.015] ${aspect}`}
    >
      <ImageIcon size={24} className="text-white/20" />
      <p className="text-[10px] font-medium uppercase tracking-[0.3em] text-white/30">
        {item.title} Coming Soon
      </p>
    </div>
  );
};

const Work = ({ onPlay }) => (
  <section id="work" data-testid="work-section" className="bg-white/[0.015] py-24 md:py-32">
    <div className="mx-auto max-w-7xl px-5 md:px-8">
      <Reveal>
        <div className="flex flex-col gap-6 md:flex-row md:items-end md:justify-between">
          <div>
            <p className="text-[11px] font-semibold uppercase tracking-[0.4em] text-cyan-300">
              My Portfolio
            </p>
            <h2
              data-testid="work-heading"
              className="font-display mt-4 text-5xl text-white sm:text-6xl lg:text-7xl"
            >
              SELECTED WORK
            </h2>
          </div>
          <p className="max-w-sm text-sm leading-relaxed text-white/50 md:text-base">
            A look at recent productions — more photography and film dropping soon.
          </p>
        </div>
      </Reveal>
      <div className="mt-14 grid gap-5 lg:grid-cols-12">
        {WORK.slice(0, 3).map((item, i) => (
          <Reveal
            key={i}
            delay={i * 120}
            className={
              item.span === "feature" ? "lg:col-span-7 lg:row-span-2" : "lg:col-span-5"
            }
          >
            <WorkCard item={item} index={i} onPlay={onPlay} />
          </Reveal>
        ))}
      </div>
      <div className="mt-5 grid gap-5 sm:grid-cols-2 lg:grid-cols-3">
        {WORK.slice(3).map((item, i) => (
          <Reveal key={i + 3} delay={i * 120}>
            <WorkCard item={item} index={i + 3} onPlay={onPlay} />
          </Reveal>
        ))}
      </div>
    </div>
  </section>
);

const About = () => (
  <section id="about" data-testid="about-section" className="relative overflow-hidden py-24 md:py-32">
    <img
      src={LOGO}
      alt=""
      aria-hidden="true"
      className="pointer-events-none absolute -right-24 top-1/2 w-[420px] -translate-y-1/2 opacity-[0.05] mix-blend-screen"
    />
    <div className="relative z-10 mx-auto grid max-w-7xl gap-12 px-5 md:px-8 lg:grid-cols-2 lg:gap-20">
      <Reveal>
        <div>
          <p className="text-[11px] font-semibold uppercase tracking-[0.4em] text-fuchsia-400">
            Who I Am
          </p>
          <h2
            data-testid="about-heading"
            className="font-display mt-4 text-5xl leading-[0.95] text-white sm:text-6xl lg:text-7xl"
          >
            CREATIVE CONTENT.
            <br />
            <span className="text-glow-cyan">BUILT TO BE</span>{" "}
            <span className="text-glow-pink">SEEN.</span>
          </h2>
        </div>
      </Reveal>
      <Reveal delay={150}>
        <div className="flex flex-col justify-center border-l border-white/10 pl-8 lg:pl-12">
          <span className="mb-6 block h-px w-16 bg-gradient-to-r from-cyan-400 to-fuchsia-500" />
          <p data-testid="about-text" className="text-base leading-relaxed text-white/60 md:text-lg">
            Crosby Media Productions is my creative venture, built around one
            simple idea - great content should make people feel something.
          </p>
          <p className="mt-5 text-base leading-relaxed text-white/60 md:text-lg">
            I create professional photo and video content that captures the
            energy, emotion and atmosphere behind your brand, event or business.
            Whether it’s the intensity of a boxing gym, the atmosphere of a live
            event, the connection between people or the story behind a brand, I
            focus on capturing the moments that make people stop, watch and
            remember.
          </p>
          <p className="mt-5 text-base leading-relaxed text-white/60 md:text-lg">
            From the first idea to the final edit, I put creativity and attention
            to detail into every project. I don’t just want to document what’s
            happening - I want to bring it to life, creating content that feels
            authentic to you and connects with the people you want to reach.
          </p>
          <p className="mt-9 text-base font-medium leading-relaxed text-white/80 md:text-lg">
            Your story. Your energy. Your emotion. Captured to be seen.
          </p>
          <img src={LOGO} alt="Crosby Media Productions logo" data-testid="about-logo" className="mt-10 w-24 mix-blend-screen" />
        </div>
      </Reveal>
    </div>
  </section>
);

const Contact = () => (
  <section
    id="contact"
    data-testid="contact-section"
    className="contact-glow noise relative overflow-hidden bg-white/[0.015] py-24 md:py-32"
  >
    <div className="relative z-10 mx-auto max-w-4xl px-5 text-center md:px-8">
      <Reveal>
        <p className="text-[11px] font-semibold uppercase tracking-[0.4em] text-cyan-300">
          Let's Create Something
        </p>
        <h2
          data-testid="contact-heading"
          className="font-display mt-4 text-5xl leading-[0.95] text-white sm:text-6xl lg:text-7xl"
        >
          READY TO BRING
          <br />
          YOUR <span className="text-glow-pink">IDEA</span> TO{" "}
          <span className="text-glow-cyan">LIFE?</span>
        </h2>
      </Reveal>
      <Reveal delay={150}>
        <a
          href={WHATSAPP_URL}
          target="_blank"
          rel="noopener noreferrer"
          data-testid="contact-phone-link"
          className="font-display mt-10 inline-block text-5xl text-white transition-all duration-300 hover:text-cyan-300 hover:drop-shadow-[0_0_20px_rgba(34,211,238,0.5)] sm:text-6xl"
        >
          {PHONE_DISPLAY}
        </a>
      </Reveal>
      <Reveal delay={250}>
        <div className="mt-12 flex flex-col justify-center gap-3 sm:flex-row sm:flex-wrap sm:gap-4">
          <a
            href={WHATSAPP_URL}
            target="_blank"
            rel="noopener noreferrer"
            data-testid="call-now-button"
            className="flex h-14 items-center justify-center gap-2 rounded-xl bg-cyan-400 px-8 text-[13px] font-bold uppercase tracking-[0.15em] text-[#05070c] transition-all duration-300 hover:bg-cyan-300 hover:shadow-[0_0_35px_-8px_rgba(34,211,238,0.7)] sm:h-[60px]"
          >
            <MessageCircle size={16} /> Message Me
          </a>
          <a
            href={INSTAGRAM_URL}
            target="_blank"
            rel="noopener noreferrer"
            data-testid="instagram-button"
            className="flex h-14 items-center justify-center gap-2 rounded-xl border border-fuchsia-500/50 px-8 text-[13px] font-bold uppercase tracking-[0.15em] text-white transition-all duration-300 hover:border-fuchsia-400 hover:bg-fuchsia-500/10 hover:shadow-[0_0_35px_-8px_rgba(236,72,153,0.5)] sm:h-[60px]"
          >
            <Instagram size={16} /> Instagram
          </a>
          <a
            href={FACEBOOK_URL}
            target="_blank"
            rel="noopener noreferrer"
            data-testid="facebook-button"
            className="flex h-14 items-center justify-center gap-2 rounded-xl border border-white/20 px-8 text-[13px] font-bold uppercase tracking-[0.15em] text-white transition-all duration-300 hover:border-cyan-400/60 hover:bg-cyan-400/10 sm:h-[60px]"
          >
            <Facebook size={16} /> Facebook
          </a>
        </div>
      </Reveal>
    </div>
  </section>
);

const Footer = () => (
  <footer data-testid="site-footer" className="border-t border-white/5 py-10">
    <div className="mx-auto flex max-w-7xl flex-col items-center gap-8 px-5 md:flex-row md:justify-between md:px-8">
      <div className="flex items-center gap-4">
        <img src={LOGO} alt="Crosby Media Productions" className="h-14 w-auto mix-blend-screen" />
        <div className="text-left">
          <p className="text-sm font-semibold text-white">Crosby Media Productions</p>
          <a
            href={WHATSAPP_URL}
            target="_blank"
            rel="noopener noreferrer"
            data-testid="footer-phone-link"
            className="text-sm text-white/50 transition-colors hover:text-cyan-300"
          >
            {PHONE_DISPLAY}
          </a>
        </div>
      </div>
      <div className="flex gap-5">
        <a
          href={INSTAGRAM_URL}
          target="_blank"
          rel="noopener noreferrer"
          data-testid="footer-instagram-link"
          aria-label="Instagram"
          className="flex h-11 w-11 items-center justify-center rounded-full border border-white/10 text-white/60 transition-all duration-300 hover:border-fuchsia-400/60 hover:text-fuchsia-400"
        >
          <Instagram size={18} />
        </a>
        <a
          href={FACEBOOK_URL}
          target="_blank"
          rel="noopener noreferrer"
          data-testid="footer-facebook-link"
          aria-label="Facebook"
          className="flex h-11 w-11 items-center justify-center rounded-full border border-white/10 text-white/60 transition-all duration-300 hover:border-cyan-400/60 hover:text-cyan-300"
        >
          <Facebook size={18} />
        </a>
      </div>
      <p className="text-xs tracking-wide text-white/35">© Crosby Media Productions</p>
    </div>
  </footer>
);

function App() {
  const [activeVideo, setActiveVideo] = useState(null);
  return (
    <div className="min-h-screen bg-[#05070c] font-body text-white">
      <Header />
      <main>
        <Hero onPlay={setActiveVideo} />
        <Strip />
        <Services onView={setActiveVideo} />
        <Work onPlay={setActiveVideo} />
        <About />
        <Contact />
      </main>
      <Footer />
      {activeVideo && <Lightbox item={activeVideo} onClose={() => setActiveVideo(null)} />}
    </div>
  );
}

export default App;
